import flatten from 'lodash-es/flatten';
import omit from 'lodash-es/omit';
import { uniqObjectMerge, uniqPrimativeMerge } from '../../utils/arrayUtils';
import { ProgramActionTypes } from './programActionTypes';
import { ProgramActionKeys as keys } from './programsActionKeys';
import {
  IProgramCashDiscount,
  IProgramGroup,
  IProgramGroupLookup,
  IProgramLevel,
  IProgramLookup,
  IProgramPreSeasonSetup
} from './programsInterfaces';

export interface IProgramsState {
  cashDiscounts: IProgramCashDiscount[];
  /**
   * Dealer Cash Discount setup hash, indexed by accountNum, then fiscalYear
   */
  dealerCashDiscounts: Record<string, Record<string, { loaded: boolean; value: number | null }>>;
  maxFiscal: string;
  /**
   * PreSeason setup hash, indexed by fiscal year, then brand
   */
  preSeasonSetup: { [index: string]: { [index: string]: IProgramPreSeasonSetup } };
  programGroups: Record<number, IProgramGroupLookup>;
  programLevels: Record<number, IProgramLevel>;
  /**
   * Programs hash, indexed by fiscal year, then brand
   */
  programs: { [index: string]: { [index: string]: IProgramLookup } };
}

const initState: IProgramsState = {
  cashDiscounts: [],
  dealerCashDiscounts: {},
  maxFiscal: '',
  preSeasonSetup: {},
  programGroups: {},
  programLevels: {},
  programs: {}
};

export function programsReducer(state: IProgramsState = initState, action: ProgramActionTypes): IProgramsState {
  let programGroupUpdate: IProgramGroupLookupUpdate;
  switch (action.type) {
    case keys.SET_MAX_PROGRAM_FISCAL:
      return {
        ...state,
        maxFiscal: action.payload
      };
    case keys.REMOVE_CASH_DISCOUNT:
      return {
        ...state,
        cashDiscounts: state.cashDiscounts.filter(x => x.programCashDiscountId !== action.payload.programCashDiscountId)
      };
    case keys.REMOVE_PROGRAM_GROUP:
      const group = state.programGroups[action.payload.programGroupId];

      if (group) {
        return {
          ...state,
          programGroups: omit(state.programGroups, action.payload.programGroupId),
          programs: {
            ...state.programs,
            [group.fiscalYear]: {
              ...state.programs[group.fiscalYear],
              [group.brand]: {
                ...state.programs[group.fiscalYear][group.brand],
                groupIds: state.programs[group.fiscalYear][group.brand].groupIds.filter(
                  x => x !== action.payload.programGroupId
                )
              }
            }
          }
        };
      }

      return state;
    case keys.REMOVE_PROGRAM_LEVEL:
      const level = state.programLevels[action.payload.programLevelId];
      console.log(level);

      if (level) {
        return {
          ...state,
          programGroups: {
            ...state.programGroups,
            [level.programGroupId]: {
              ...state.programGroups[level.programGroupId],
              levelIds: state.programGroups[level.programGroupId].levelIds.filter(
                x => x !== action.payload.programLevelId
              )
            }
          },
          programLevels: omit(state.programLevels, action.payload.programLevelId)
        };
      }

      return state;
    case keys.SET_CASH_DISCOUNTS:
      return {
        ...state,
        cashDiscounts: uniqObjectMerge(state.cashDiscounts, action.payload, x => x.programCashDiscountId)
      };
    case keys.SET_DEALER_CASH_DISCOUNT:
      return {
        ...state,
        dealerCashDiscounts: {
          ...state.dealerCashDiscounts,
          [action.payload.accountNum]: {
            ...state.dealerCashDiscounts[action.payload.accountNum],
            [action.payload.fiscalYear]: {
              loaded: true,
              value: action.payload.programCashDiscountId
            }
          }
        }
      };
    case keys.SET_PRESEASON_PROGRAM_SETUP:
      return {
        ...state,
        preSeasonSetup: action.payload.reduce<{ [index: string]: { [index: string]: IProgramPreSeasonSetup } }>(
          (hash, ps) => {
            hash[ps.fiscalYear] = {
              ...hash[ps.fiscalYear],
              [ps.brand]: ps
            };
            return hash;
          },
          { ...state.preSeasonSetup }
        )
      };
    case keys.SET_PROGRAM_GROUPS:
      programGroupUpdate = getGroupLookupAndLevels(action.payload);

      return {
        ...state,
        programGroups: {
          ...state.programGroups,
          ...programGroupUpdate.programGroups
        },
        programLevels: {
          ...state.programLevels,
          ...programGroupUpdate.programLevels
        },
        programs: action.payload.reduce<{ [index: string]: { [index: string]: IProgramLookup } }>((hash, g) => {
          const existing = hash[g.fiscalYear][g.brand];
          if (existing) {
            hash[existing.fiscalYear][existing.brand] = {
              ...existing,
              groupIds: uniqPrimativeMerge(existing.groupIds, g.programGroupId)
            };
          }

          return hash;
        }, state.programs)
      };
    case keys.SET_PROGRAM_LEVELS:
      return {
        ...state,
        programGroups: action.payload.reduce<Record<number, IProgramGroupLookup>>((hash, item) => {
          const match = hash[item.programGroupId];
          if (match) {
            hash[item.programGroupId] = {
              ...match,
              levelIds: uniqPrimativeMerge(match.levelIds, item.programLevelId)
            };
          }
          return hash;
        }, state.programGroups),
        programLevels: action.payload.reduce<Record<number, IProgramLevel>>((hash, item) => {
          hash[item.programLevelId] = item;
          return hash;
        }, state.programLevels)
      };
    case keys.SET_PROGRAMS:
      programGroupUpdate = getGroupLookupAndLevels(flatten(action.payload.map(x => x.groups)));

      return {
        ...state,
        programGroups: {
          ...state.programGroups,
          ...programGroupUpdate.programGroups
        },
        programLevels: {
          ...state.programLevels,
          ...programGroupUpdate.programLevels
        },
        programs: {
          ...state.programs,
          ...action.payload.reduce<{ [index: string]: { [index: string]: IProgramLookup } }>(
            (hash, p) => {
              hash[p.fiscalYear] = {
                ...hash[p.fiscalYear],
                [p.brand]: {
                  brand: p.brand,
                  dealerEndDate: p.dealerEndDate,
                  dealerStartDate: p.dealerStartDate,
                  employeeEndDate: p.employeeEndDate,
                  employeeStartDate: p.employeeStartDate,
                  fiscalYear: p.fiscalYear,
                  groupIds: p.groups.map(x => x.programGroupId)
                }
              };

              return hash;
            },
            { ...state.programs }
          )
        }
      };
    default:
      return state;
  }
}

interface IProgramGroupLookupUpdate {
  programGroups: Record<number, IProgramGroupLookup>;
  programLevels: Record<number, IProgramLevel>;
}

function getGroupLookupAndLevels(groups?: IProgramGroup[]): IProgramGroupLookupUpdate {
  if (groups) {
    const programLevels = flatten(groups.map(y => y.levels));

    return {
      programGroups: groups.reduce<Record<number, IProgramGroupLookup>>((hash, g) => {
        hash[g.programGroupId] = {
          access: g.access,
          brand: g.brand,
          fiscalYear: g.fiscalYear,
          levelIds: g.levels.map(x => x.programLevelId),
          name: g.name,
          programGroupId: g.programGroupId
        };
        return hash;
      }, {}),
      programLevels: programLevels.reduce<Record<number, IProgramLevel>>((hash, l) => {
        hash[l.programLevelId] = l;
        return hash;
      }, {})
    };
  }
  return { programGroups: [], programLevels: [] };
}
